import { Loader } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Button } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import UnknownErrorScreen from '~/domains/common/components/UnknownErrorScreen/UnknownErrorScreen';

import { ContestManageModalBody, ContestManageModalTitle } from '../commonStyles';

import { usePricingInfoModalStyles } from './style';
import { usePricingProxy } from './usePricingProxy';

export interface PricingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  leagueAlias?: string;
  contestType?: string;
  brand?: string;
}

function PricingInfoModal({
  isOpen,
  onClose,
  leagueAlias,
  contestType,
  brand,
}: PricingInfoModalProps) {
  const { t } = useTranslation('contest');
  const { classes, theme } = usePricingInfoModalStyles();

  const {
    isLoading: isLoadingQuery,
    error: proxyError,
    isSuccess,
    data: validatedResponse,
    iframeLoaded,
    pricingUrl,
    setIframeLoaded,
  } = usePricingProxy({
    leagueAlias,
    contestType,
    brand,
    isOpen,
  });

  const isLoading = isLoadingQuery || (isSuccess && !proxyError && !iframeLoaded);

  return (
    <Dialog
      type={{
        desktop: 'modal',
        mobile: 'drawer',
      }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ContestManageModalBody>
        <ContestManageModalTitle>
          {t('manage.restart.pricingInfoModal.title')}
        </ContestManageModalTitle>
        <div className={classes.iframeContainer}>
          {proxyError && !isLoading && <UnknownErrorScreen statusCode={404} hideActions />}
          {!proxyError && !isLoadingQuery && validatedResponse && (
            <iframe
              src={pricingUrl}
              className={classes.iframe}
              title="Pricing Content"
              onLoad={() => setIframeLoaded(true)}
            />
          )}
          {isLoading && (
            <div className={classes.loadingOverlay}>
              <Loader color={theme.colors.blue[5]} size="lg" />
            </div>
          )}
        </div>

        <Button variant="primary-fill" fullWidth className={classes.button} onClick={onClose}>
          {t('manage.restart.pricingInfoModal.cta')}
        </Button>
      </ContestManageModalBody>
    </Dialog>
  );
}

export default PricingInfoModal;
